import { ChatMemoryDto } from 'src/api/FiretrolApiClient';
import { ChatMemoryAction } from 'src/api/queries/chat/useChatConversationQueries';
import { capitalize, parseDate } from 'src/utils';

/** Server limit on a note's text after trimming. */
export const MEMORY_TEXT_MAX_LENGTH = 200;

export const MEMORY_SECTION_ID = 'fire-memory';
export const MEMORY_PROFILE_PATH = `/account/profile#${MEMORY_SECTION_ID}`;

export const MEMORY_KIND_OPTIONS = [
  { value: 'preference', label: 'Preference' },
  { value: 'focus', label: 'Focus' },
  { value: 'alias', label: 'Alias' },
  { value: 'default', label: 'Default' },
];

export const MEMORY_KINDS = MEMORY_KIND_OPTIONS.map((option) => option.value);

const SOURCE_LABELS: Record<string, string> = {
  user_request: 'Asked to remember',
  suggestion: 'Suggested by F.I.R.E.',
  manual: 'Added manually',
};

const STATUS_LABELS: Record<string, string> = {
  proposed: 'Suggested',
  active: 'Active',
  declined: 'Declined',
};

export const memoryKindLabel = (kind: string): string =>
  MEMORY_KIND_OPTIONS.find((option) => option.value === kind)?.label ??
  capitalize(kind);

export const memorySourceLabel = (source: string): string =>
  SOURCE_LABELS[source] ?? capitalize(source.replace(/_/g, ' '));

export const memoryStatusLabel = (status: string): string =>
  STATUS_LABELS[status] ?? capitalize(status);

const STALE_AFTER_MS = 30 * 24 * 60 * 60 * 1000;

/** True when no reply has used the note in the last 30 days; a note never used counts from when it was saved. */
export const isMemoryStale = (
  note: ChatMemoryDto,
  now: Date = new Date(),
): boolean => {
  const lastTouched = parseDate(note.lastUsedAt ?? note.createdAt);
  return (
    !!lastTouched && now.getTime() - lastTouched.getTime() > STALE_AFTER_MS
  );
};

/** One-line label for a memory segment in an export or print, from what the reply did and the note as it left it. */
export const memorySegmentLabel = (
  memory: ChatMemoryDto,
  action: ChatMemoryAction,
): string => {
  if (action === 'removed') return 'Removed from memory';
  if (memory.status === 'proposed') return 'Suggested for memory';
  if (memory.status === 'declined') return 'Not saved to memory';
  return 'Saved to memory';
};
